import * as logger from '@/utils/logger'

import type { RundownCueOrderItem } from '~shared/rundown/rundown'

import {
  groupRundownCues,
  reorderRundownCues,
} from '@/entities/rundown/api/requests'

import { getRundown, requireRundown } from '../data/rundown.store'

import { pendingCueOrderActions } from './cue-order-pending.store'
import { flushCuesDerived, getCueOrder } from './cue.derived'
import { CueDropzone, getCueDropzone, getTargetCueId } from './cue.dnd'
import { moveCueToGroup, reorderCues } from './cue.order'

import type { DragEndEvent } from '@dnd-kit/core'

/**
 * The save of the most recent reorder. Never rejects. The group path waits on
 * it so the server applies the reorder before the group is built.
 */
let cueOrderSave: Promise<void> = Promise.resolve()

/**
 * Forget the last reorder. Called when leaving a rundown, so the next rundown's
 * group drop does not wait on a save that belongs to the previous one.
 */
export function resetCueOrderSave() {
  cueOrderSave = Promise.resolve()
}

/**
 * Send a new cue order, rolling back if the server rejects it.
 *
 * The order is shown before the request goes out, so the cue stays where the
 * user dropped it instead of snapping back for a round trip. It is held as a
 * pending order until the listener delivers it. Applied synchronously, so the
 * drag overlay animates into the new position rather than back to the old one:
 * see {@link flushCuesDerived}.
 */
function applyCueOrder(rundownId: string, nextCueOrder: RundownCueOrderItem[]) {
  pendingCueOrderActions.set(nextCueOrder)
  flushCuesDerived()

  cueOrderSave = saveCueOrder(rundownId, nextCueOrder)
  return cueOrderSave
}

async function saveCueOrder(
  rundownId: string,
  nextCueOrder: RundownCueOrderItem[],
) {
  try {
    await reorderRundownCues({ rundownId, cues: nextCueOrder })
    pendingCueOrderActions.markSaved(nextCueOrder)
  } catch (error) {
    logger.error('[dropCue] reorder failed, restoring previous order', error)

    // guard: the session may have moved on to another rundown
    if (getRundown()?.id !== rundownId) return

    // Nothing was saved, so dropping the pending order is the rollback.
    pendingCueOrderActions.rollback(nextCueOrder)
    flushCuesDerived()
  }
}

/**
 * Apply a dropped cue.
 *
 * A plain function rather than a component callback: nothing here needs React,
 * so `CuesList` only has to hand over the dnd-kit event.
 *
 * @remarks
 *   The rundown store is not written here. The order is shown as a pending
 *   order (see `cue-order-pending.store`) until the listener delivers it.
 */
export async function dropCue({ active, over }: DragEndEvent) {
  if (!over) return

  const rundown = requireRundown()

  const sourceCueId = String(active.id)
  const dropzone = getCueDropzone(String(over.id))
  const targetCueId = getTargetCueId(String(over.id))

  if (!dropzone || !targetCueId) return

  // guard: don't allow drops onto self
  if (sourceCueId === targetCueId) return

  // Built from what is on screen, which may still be a pending drop.
  const currentCueOrder = getCueOrder()

  // Dropped onto a plain cue: the two become a new group. Server-side, because
  // it has to mint the group cue, so there is no order to apply up front.
  if (dropzone === CueDropzone.CUE) {
    // A reorder may still be in flight. Waiting for it means the group is built
    // on the reordered rundown and the listener shows the group, instead of the
    // pending reorder hiding it.
    await cueOrderSave

    // guard: the session may have moved on to another rundown while waiting
    // currentCueOrder is stale after this await and must not be used (A->B->A passes the id check).
    if (getRundown()?.id !== rundown.id) return

    try {
      await groupRundownCues({
        rundownId: rundown.id,
        beforeCueId: targetCueId,
        cuesToGroupIds: [targetCueId, sourceCueId],
      })
    } catch (error) {
      logger.error('[dropCue] grouping failed', error)
    }
    return
  }

  const nextCueOrder =
    dropzone === CueDropzone.GROUP
      ? // Dropped onto an existing group: the cue becomes its first child.
        moveCueToGroup(currentCueOrder, sourceCueId, targetCueId)
      : // Dropped between cues.
        reorderCues(
          currentCueOrder,
          sourceCueId,
          targetCueId,
          dropzone === CueDropzone.AFTER,
        )

  // Both operations hand back the order untouched when the move is impossible,
  // usually because the target was removed mid-drag. Nothing to send.
  if (nextCueOrder === currentCueOrder) {
    logger.warn('[dropCue] drop target is gone, order unchanged', {
      dropzone,
      sourceCueId,
      targetCueId,
    })
    return
  }

  await applyCueOrder(rundown.id, nextCueOrder)
}
